// Values are contractual (stored in the DB, read by the API): do not rename them.

export const ROLES = {
  SALES_ASSOCIATE: 'sales_associate',
  BOUTIQUE_MANAGER: 'boutique_manager',
  WORKSHOP: 'workshop',
  ADMIN: 'admin',
  HEAD_OFFICE: 'head_office',
  CALL_CENTER: 'call_center',
  DHL: 'dhl',
} as const;

export type Role = (typeof ROLES)[keyof typeof ROLES];

export const PLATFORM = {
  ABCDIOR: 'abcdior',
  DMS: 'dms',
} as const;

export type Platform = (typeof PLATFORM)[keyof typeof PLATFORM];

/** URL prefix of the DMS platform, surfaced to users as "MTM". */
export const MTM_ROUTE_PATH = 'mtm';

/** Product lines of the DMS (made-to-measure) platform. Values are stored on the items. */
export const DMS_PRODUCTLINE = {
  SUIT: 'SUIT',
  JACKET: 'JACKET',
  PANTS: 'PANTS',
  SHIRT: 'SHIRT',
  WAISTCOAT: 'WAISTCOAT',
  COAT: 'COAT',
  MORNING_SUIT: 'MORNING_SUIT',
} as const;

export type DmsProductLine =
  (typeof DMS_PRODUCTLINE)[keyof typeof DMS_PRODUCTLINE];

export const DMS_PRODUCTLINES: { label: string; value: DmsProductLine }[] = [
  { label: 'Suits', value: DMS_PRODUCTLINE.SUIT },
  { label: 'Jackets', value: DMS_PRODUCTLINE.JACKET },
  { label: 'Pants', value: DMS_PRODUCTLINE.PANTS },
  { label: 'Shirts', value: DMS_PRODUCTLINE.SHIRT },
  { label: 'Waistcoats', value: DMS_PRODUCTLINE.WAISTCOAT },
  { label: 'Coats', value: DMS_PRODUCTLINE.COAT },
  { label: 'Morning Suit', value: DMS_PRODUCTLINE.MORNING_SUIT },
];

/** Fits of a DMS model. */
export const DMS_FITS = [
  'ADJUSTED FIT',
  'ADJUSTED FIT / STRAIGHT FITTED CUT',
  'BLAZER',
  'DROP 7',
  'DROP 8',
  'DROP 10',
  'DROP 10 / 8',
  'MORNING SUIT',
  'REGULAR CUT',
  'SHORT CUT',
] as const;

/** Element types of the DMS platform. */
export const DMS_ELEMENT_TYPES = [
  'BUTTON',
  'BUTTONING TAB',
  'COLLAR',
  'CUFF',
  'EMBROIDERY',
  'HEM',
  'LINING',
  'OPTION',
  'SHIRT BUTTON',
  'SLEEVE LINING',
  'SOUTACHE',
] as const;

export type DmsElementType = (typeof DMS_ELEMENT_TYPES)[number];

/** Event types of the DMS platform. */
export const DMS_EVENT_TYPE = {
  VILLA_DIOR: 'VILLA DIOR',
  EVENT: 'EVENT',
  BOUTIQUE: 'BOUTIQUE',
} as const;

/** `undefined` for an unknown value. */
export const getDmsProductLineLabel = (value: string): string | undefined =>
  DMS_PRODUCTLINES.find((line) => line.value === value)?.label;

export const STORE_ALL = {
  id: -1,
  name: 'ALL',
  region: 'ALL',
  country: 'ALL',
} as const;

/** Keys of the localStorage session and caches. */
export const STORAGE_KEYS = {
  TOKEN: 'id_token',
  USER: 'user',
  APP_CONFIG: 'app_config',
  SELECTED_STORES: 'selectedStores',
  LOGIN_ERROR_CODE: 'loginErrorCode', // sessionStorage
} as const;

/** Exclusive symbols a store can enable. `code` is the glyph in the DiorSymboles font. */
export const ALLOWED_EXCLUSIVE_SYMBOLS = [
  {
    id: 'montaigneExclusiveSymbolActive',
    key: 'montaigne',
    value: '30 Montaigne',
    code: '002A',
  },
  {
    id: 'triumphExclusiveSymbolActive',
    key: 'triumph',
    value: 'Triumph Arch',
    code: '0040',
  },
  { id: 'doveExclusiveSymbolActive', key: 'dove', value: 'Dove', code: '003F' },
  {
    id: 'eiffelExclusiveSymbolActive',
    key: 'eiffel',
    value: 'Eiffel Tower',
    code: '003E',
  },
  {
    id: 'gardenExclusiveSymbolActive',
    key: 'garden',
    value: 'Garden',
    code: '002F',
  },
  { id: 'roseExclusiveSymbolActive', key: 'rose', value: 'Rose', code: '003C' },
  {
    id: 'parrotExclusiveSymbolActive',
    key: 'parrot',
    value: 'Parrot',
    code: '0022',
  },
  {
    id: 'pampanoExclusiveSymbolActive',
    key: 'pampano',
    value: 'Pampano',
    code: '002B',
  },
  { id: 'lilyExclusiveSymbolActive', key: 'lily', value: 'Lily', code: '003D' },
  {
    id: 'ariesExclusiveSymbolActive',
    key: 'aries',
    value: 'Aries',
    code: '00B5',
  },
  {
    id: 'taurusExclusiveSymbolActive',
    key: 'taurus',
    value: 'Taurus',
    code: '007B',
  },
  {
    id: 'geminiExclusiveSymbolActive',
    key: 'gemini',
    value: 'Gemini',
    code: '00B0',
  },
  {
    id: 'cancerExclusiveSymbolActive',
    key: 'cancer',
    value: 'Cancer',
    code: '005B',
  },
  { id: 'leoExclusiveSymbolActive', key: 'leo', value: 'Leo', code: '005D' },
  {
    id: 'virgoExclusiveSymbolActive',
    key: 'virgo',
    value: 'Virgo',
    code: '00B2',
  },
  {
    id: 'libraExclusiveSymbolActive',
    key: 'libra',
    value: 'Libra',
    code: '007C',
  },
  {
    id: 'scorpiusExclusiveSymbolActive',
    key: 'scorpius',
    value: 'Scorpius',
    code: '00E7',
  },
  {
    id: 'sagittariusExclusiveSymbolActive',
    key: 'sagittarius',
    value: 'Sagittarius',
    code: '00F9',
  },
  {
    id: 'capricornusExclusiveSymbolActive',
    key: 'capricornus',
    value: 'Capricornus',
    code: '00E0',
  },
  {
    id: 'aquariusExclusiveSymbolActive',
    key: 'aquarius',
    value: 'Aquarius',
    code: '00A7',
  },
  {
    id: 'piscesExclusiveSymbolActive',
    key: 'pisces',
    value: 'Pisces',
    code: '00E8',
  },
  {
    id: 'butterflyExclusiveSymbolActive',
    key: 'butterfly',
    value: 'Butterfly',
    code: '00E9',
  },
] as const;

export type ExclusiveSymbolKey =
  (typeof ALLOWED_EXCLUSIVE_SYMBOLS)[number]['key'];

/** Keyboards a product line can enable. */
export const KEYBOARDS = {
  latin: 'Latin',
  chinese: 'Simplified Chinese',
  chineseTw: 'Traditional Chinese (Taiwan)',
  chineseHk: 'Traditional Chinese (Hong Kong)',
  arabic: 'Arabic',
  cyrillic: 'Cyrillic',
} as const;

export type KeyboardKey = keyof typeof KEYBOARDS;

export const SPECIAL_CHARS = {
  numbers: 'Numbers (1,2,3...)',
  dot: '.',
  dash: '-',
  oneSpaceBetweenChars: '1 space between characters',
} as const;

export const ALLOWED_CASES = {
  upperCase: 'UPPERCASE',
  lowerCase: 'lowercase',
  titleCase: 'Capitalize Each Word',
} as const;

/** Iconic symbols: key -> glyph code in the DiorSymboles font. */
export const ALLOWED_ICONIC_SYMBOLS = {
  eight: '0021',
  bee: '0023',
  heart: '0024',
  star: '0025',
  clover: '0026',
} as const;

/** Fonts of the latin keyboard and their weights. */
export const AVAILABLE_FONTS: Record<string, readonly string[]> = {
  DinCondensed: ['Regular', 'Medium', 'Bold'],
  MTCORSVA: ['Regular', 'Bold'],
  DiorBaby: ['Regular'],
  Cochin: ['Bold'],
};

export const BOOK_TOTE = 'Book Tote';
